import {
  DashboardState,
  EAppSupportType,
  EProjectType,
  EPropertyType,
  ESdChangeReason,
  IProperty
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import type { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { IApplyPropertyGridChangesToDisplayParameters } from "../../../../models/interface/function-parameters";
import confJson from "../../../../shared/data/conf.json";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IPureDesignComponentProps, PureSdComponent } from "../../../../shared/pure-sd";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import { applyDesignerPropertyChange } from "../../../../shared/utility/designer-property-actions";
import {
  scanColumnDataFields,
  shouldShowDefaultDataField
} from "../../../../shared/utility/designer-property-data-editor-actions";
import { tryResolveEditTarget } from "../../../../shared/utility/detached-screen-session";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import { JsonEditorModal } from "../../../management/settings/component-set/json-editor-modal";
import { EXPANDED_PROPERTY_SECTION, focusFirstEditableControl } from "./focus-first-property-control";
import { PropertyFormItem } from "./property-form-item";
import { PropertyPanel } from "./property-panel";

const { kuikaDevelopers } = confJson;

declare let window: any;
export interface IPropertyFormState {
  isJsonEditorModalVisible: boolean;
  mappedProperty: IProperty;
  propertyToBeEdit: IProperty;
}

export interface IPropertyFormProps {
  isCalledFromActionPicker?: boolean;
  kuikaId?: string;
  location: "NavigationPanel" | "PropertyPanel";
  menuSelectValue?: string;
  /**
   * The screen these properties belong to, when it is NOT the one the designer has open — the
   * workflow screen builder edits a task screen chosen from a list (KOA-18431). Absent, this form
   * behaves exactly as the property panel always has.
   */
  screenId?: string | null;
  /** Fired after a property write lands, so a caller staging changes can record it. */
  onPropertyChanged?: (kuikaId: string, propertyName: string) => void;
  /**
   * Bump this to force a re-read after the edited screen changed underneath the form.
   *
   * Needed only for a screen the designer does not have open: that screen's tree is a plain
   * in-memory object, so a write to it produces no context change and the SD-driven update gate
   * would keep showing the value from before the write. The host bumps the token after each
   * landed change; the open-screen panel needs nothing, because its writes dispatch a change
   * reason the gate already recognises.
   */
  refreshToken?: number;
}

class PropertyForm extends PureSdComponent<IPropertyFormProps & IWithSDProps & IWithContextProp, IPropertyFormState> {
  activeParameterPropertyName: string = "";

  propertySdHelper: PropertySdHelper;

  constructor(props: IPropertyFormProps & IWithSDProps & IWithContextProp) {
    super(props);
    const sVar = {
      isJsonEditorModalVisible: false,
      propertyToBeEdit: {} as IProperty,
      mappedProperty: {} as IProperty
    };
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
    this.state = sVar;
  }

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IPropertyFormProps & IWithContextProp>
  ): void {
    if (prevProps.kuikaId !== this.props.kuikaId) {
      setTimeout(() => {
        if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
          return;
        }
        focusFirstEditableControl(document.querySelector(EXPANDED_PROPERTY_SECTION));
      }, 200);
    }
  }

  getProperties = (): IProperty[] | undefined => {
    if (this.props.kuikaId === undefined) {
      return [];
    }
    let properties: IProperty[] | undefined;
    const { kuikaId } = this.props;
    properties = this.propertySdHelper.getProperties(kuikaId);
    properties = properties?.filter(
      (p: IProperty) =>
        p.selectedType !== EPropertyType.event && (p.isTechnical !== true || this.isUserKuikaDeveloper() === true)
    );
    return properties;
  };

  isUserKuikaDeveloper = () => {
    return KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username);
  };

  /**
   * The context the property list is read through: the open screen, or the named one.
   *
   * Never falls back to the open screen when a named screen is unavailable — listing one screen's
   * properties while the user looks at another is exactly the confusion this seam prevents. The
   * form renders nothing in that case (an empty property list).
   */
  getScreenDesignContextProviderValues = (): IScreenDesignContextState => {
    if (!this.props.screenId) return this.props.sd;
    const target: IScreenDesignContextState | undefined = tryResolveEditTarget(getSdInstance(), this.props.screenId);
    return target ?? ({ screenDesignDictionary: {} } as IScreenDesignContextState);
  };

  applyPropertyValueChanged = (params: IApplyPropertyGridChangesToDisplayParameters) => {
    applyDesignerPropertyChange(getSdInstance(), params, ESdChangeReason.ChangePropertyValue, this.props.screenId);
    if (params.kuikaId && params.propertyName) {
      this.props.onPropertyChanged?.(params.kuikaId, params.propertyName);
    }
  };

  toggleJsonEditor = (property?: IProperty) => {
    let mP = {} as IProperty;
    if (property) {
      if (property.typeOptions && property.typeOptions.length > 0) {
        const jsonAttrProperty =
          property.typeOptions.find &&
          property.typeOptions.find((x) => x.propertyType === EPropertyType.object.toString());
        if (jsonAttrProperty && jsonAttrProperty.hasOwnJSONSchema === false) {
          const properties = this.getProperties();
          const mappedProperty =
            properties?.find && properties.find((p) => p.name === jsonAttrProperty.connectedPropertyName);
          if (mappedProperty) {
            mP = mappedProperty;
          }
        }
      }
      this.setState({
        propertyToBeEdit: property,
        mappedProperty: mP,
        isJsonEditorModalVisible: !this.state.isJsonEditorModalVisible
      });
    } else {
      this.setState({
        propertyToBeEdit: {} as IProperty,
        mappedProperty: {} as IProperty,
        isJsonEditorModalVisible: !this.state.isJsonEditorModalVisible
      });
    }
  };

  getIsExpandedPropertyName = (properties: IProperty[] | undefined): string => {
    let result: string = "";
    const lastExpandedProperties = _.clone(this.props.sd.expandedProperties);
    const revesedExpandedProperties = lastExpandedProperties.reverse();
    for (let index = 0; index < revesedExpandedProperties.length; index++) {
      const expandedPropertyName = revesedExpandedProperties[index];
      const p = properties?.find((p) => p.name === expandedPropertyName);
      if (p !== undefined) {
        result = p.name;
        break;
      }
    }
    return result;
  };

  handleChangeExpandedPanel = (propertyName: string, isExpandedPanel: boolean) => {
    if (isExpandedPanel === true) {
      if (this.props.sd.addItemToExpandedPropertyList) {
        this.props.sd.addItemToExpandedPropertyList(propertyName);
        setTimeout(() => {
          if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
            return;
          }
          focusFirstEditableControl(document.querySelector(EXPANDED_PROPERTY_SECTION));
        }, 200);
      }
    } else if (this.props.sd.removeItemFromExpandedPropertyList) {
      this.props.sd.removeItemFromExpandedPropertyList(propertyName);
    }
  };

  // Whether a property gets a row at all. Asked before the rows are laid out, not inside one:
  // the sections are a grouped card now, where the first and last row take the well's corners and
  // every row after the first draws a divider — a row rendered empty would claim both.
  isPropertyVisible = (p: IProperty): boolean => {
    if (this.props.kuikaId === undefined) {
      return false;
    }
    if (this.props.isCalledFromActionPicker === true && p.isVisibleProp) {
      return false;
    }
    const isWebScreen: boolean = this.props.sd.getIsWebScreen();
    if (
      p.appSupportType &&
      p.appSupportType === EAppSupportType.Web &&
      this.props.sd.project.projectType !== EProjectType.Desktop &&
      !isWebScreen
    ) {
      return false;
    }
    if (
      p.appSupportType &&
      p.appSupportType === EAppSupportType.Mobile &&
      this.props.sd.project.projectType === EProjectType.Desktop
    ) {
      return false;
    }
    if (p.name === "defaultDataField") {
      // `defaultDataField` only matters when a TableTemplateColumn binds 2+
      // distinct fields and the table runtime can no longer auto-pick the
      // column's sort/search field. The scan walks the column at any depth
      // (so Panel-nested content counts) and excludes styling-only bindings
      // such as dynamicBackGroundColor from the selectable content fields.
      // KOA-17569.
      const di = this.getScreenDesignContextProviderValues().screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
      const scan = scanColumnDataFields(di?.children);
      if (!shouldShowDefaultDataField(scan)) {
        return false;
      }
    }
    return true;
  };

  getFormItem = (p: IProperty, expanded: boolean) => {
    const kuikaId: string | undefined = this.props.kuikaId;
    if (kuikaId === undefined || !this.isPropertyVisible(p)) {
      return null;
    }
    return (
      <PropertyPanel
        isExpandedPanel={expanded}
        onChangeExpandedPanel={(isExpandedPanel: boolean) => {
          this.handleChangeExpandedPanel(p.name, isExpandedPanel);
        }}
        location={this.props.location}
        property={p}
      >
        <PropertyFormItem
          kuikaId={kuikaId}
          property={p}
          toggleJsonModal={this.toggleJsonEditor}
          screenId={this.props.screenId}
          onPropertyChanged={this.props.onPropertyChanged}
          refreshToken={this.props.refreshToken}
        />
      </PropertyPanel>
    );
  };

  // The rows one group holds: the properties of that kind that actually render, in their own order.
  getVisibleProperties = (properties: IProperty[] | undefined, isAdvanced: boolean): IProperty[] => {
    if (properties === undefined) {
      return [];
    }
    return properties.filter((p: IProperty): boolean => {
      if ((p.isAdvanced === true) !== isAdvanced) {
        return false;
      }
      // Static options are edited in the children panel, not here — a basic-list rule since the
      // list was split, so it stays one.
      if (!isAdvanced && this.props.menuSelectValue === "static" && p.name === "options") {
        return false;
      }
      return this.isPropertyVisible(p);
    });
  };

  renderPropertyGroup = (
    properties: IProperty[],
    keyPrefix: string,
    expandedPropertyName: string,
    isGrouped: boolean
  ) => {
    if (properties.length === 0) {
      return null;
    }
    return (
      <div className={isGrouped ? "kuika-right-panel__group kuika-right-panel__group--property" : undefined}>
        {properties.map((p: IProperty) => (
          <div
            key={`${keyPrefix}_${this.props.kuikaId}_${p.name}`}
            className={`k_Regular-G1-Left kuika_propertyFormComp_property_${p.name}${
              isGrouped ? " kuika-right-panel__group-row" : ""
            }`}
            style={isGrouped ? undefined : { marginBottom: 12 }}
          >
            {this.getFormItem(p, expandedPropertyName === p.name)}
          </div>
        ))}
      </div>
    );
  };

  render() {
    const properties: IProperty[] | undefined = this.getProperties();
    const expandedPropertyName: string = this.getIsExpandedPropertyName(properties);
    // In the properties column the sections are one grouped card, the same well the panels below
    // them (Authorization, Visibility) are built from; the navigation panel lists bare editors and
    // keeps its own per-row spacing.
    const isGrouped: boolean = this.props.location === "PropertyPanel";
    return (
      <>
        {this.state.isJsonEditorModalVisible && (
          <JsonEditorModal
            toggleJsonModal={this.toggleJsonEditor}
            property={this.state.propertyToBeEdit}
            mappedProperty={this.state.mappedProperty}
            applyPropertyValueChanged={this.applyPropertyValueChanged}
            components={this.props.sd.project.componentSetObj.components}
            language={this.props.sd.project.componentSetObj.language}
          />
        )}

        {/* <div style={{ margin: "10px 0px 20px 8px" }}>
          <span className="kuika_rightPanelDIName k_Medium-G2-Left">{this.propertySdHelper.getComponentName(this.props.kuikaId)}</span>
        </div> */}

        <div
          className="kuika_propertyFormComp"
          style={{
            // The column's own 16/8 inset belongs to the groups here, which carry it as their
            // margin exactly as every other group in this column does; layout.scss's 6px would sit
            // inside that and hold the first section off the panel's rhythm. The navigation panel
            // keeps the 6px block padding and flush sides it has always had.
            padding: isGrouped ? 0 : undefined,
            paddingLeft: 0,
            paddingRight: 0
          }}
        >
          {this.renderPropertyGroup(
            this.getVisibleProperties(properties, false),
            "bp",
            expandedPropertyName,
            isGrouped
          )}
          {this.renderPropertyGroup(this.getVisibleProperties(properties, true), "ap", expandedPropertyName, isGrouped)}
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason, nextProps?: IPropertyFormProps): boolean => {
    // A detached screen's tree is not React state — the host's token is the only signal that the
    // properties this form is showing have moved on.
    if (nextProps !== undefined && nextProps.refreshToken !== this.props.refreshToken) {
      return true;
    }
    if (
      changeReason === ESdChangeReason.ChangePropertyType ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.ExpandedProperties ||
      changeReason === ESdChangeReason.ChangeResponsiveRowColumnSettings ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const propertyForm = withContext<IPropertyFormProps>(withSD<IPropertyFormProps>(PropertyForm));
export { propertyForm as PropertyForm };
